/**
 * KANGA TCG — scroll reveal
 *
 * Adds `.kanga-reveal--in` to `[data-kanga-reveal]` elements as they scroll
 * into view. The hidden state lives behind `.kanga-reveal-ready` on <html>
 * (set by snippets/kanga-tokens.liquid), so this file failing to load leaves
 * every section fully visible rather than blank.
 *
 * No dependencies. ~1KB.
 */
(() => {
  const SELECTOR = '[data-kanga-reveal]';
  const REVEALED = 'kanga-reveal--in';
  const READY = 'kanga-reveal-ready';

  const root = document.documentElement;

  /** Reveal everything and stop gating — the safety net for every failure path. */
  const revealAll = () => {
    document.querySelectorAll(SELECTOR).forEach((el) => el.classList.add(REVEALED));
    root.classList.remove(READY);
  };

  // Reduced motion, no IntersectionObserver, or the gate was never set:
  // show the content and do nothing else.
  if (
    !('IntersectionObserver' in window) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !root.classList.contains(READY)
  ) {
    revealAll();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(REVEALED);
        observer.unobserve(entry.target);
      }
    },
    {
      // Start the transition slightly before the element reaches the fold so
      // it has finished by the time it is properly in view.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  /**
   * Stagger children of a container so a grid reveals as a wave rather than
   * all at once. Capped so long lists never accumulate a visible lag.
   */
  const applyStagger = (el) => {
    const step = Number(el.dataset.kangaRevealStagger);
    if (!step) return;

    Array.from(el.children).forEach((child, i) => {
      child.style.setProperty('--kanga-reveal-delay', `${Math.min(i * step, 400)}ms`);
    });
  };

  const observe = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.dataset.kangaRevealBound) return;
      el.dataset.kangaRevealBound = 'true';
      applyStagger(el);
      observer.observe(el);
    });
  };

  observe();

  // Sections added by the theme editor or the Section Rendering API.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) observe(event.target);
  });

  // Last resort: if something above went wrong and content is still hidden
  // well after load, show it rather than leave the page looking empty.
  window.addEventListener('load', () => {
    setTimeout(() => {
      const stuck = document.querySelectorAll(`${SELECTOR}:not(.${REVEALED})`);
      if (stuck.length && !stuck[0].dataset.kangaRevealBound) revealAll();
    }, 2000);
  });
})();
